import { createApp } from 'vue'
import { createPinia } from 'pinia';
import * as Sentry from "@sentry/vue";
import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import ToastService from "primevue/toastservice";
import 'primeicons/primeicons.css';
import Ripple from 'primevue/ripple';
import StyleClass from 'primevue/styleclass';
import ConfirmationService from 'primevue/confirmationservice';
import BadgeDirective from 'primevue/badgedirective';
import Tooltip from 'primevue/tooltip';
import './style.css'
import App from './App.vue'
import router from './router/index.js';
import { useAuthStore} from "./stores/authStore";
import { useAutomationStore } from "./stores/automationStore";
import { PERMISSIONS } from "./constants/permissions";
import backendCall from "./services/AxiosService";
// useSocketStore imported for consistent socket initialization (used elsewhere in app lifecycle)
import { useSocketStore as _useSocketStore } from "./stores/socketStore";


export const app = createApp(App);



const pinia = createPinia();




app.use(pinia);

app.use(PrimeVue, {
    theme: {
        preset: Aura,
        options: {
            darkModeSelector: '.dark',
            cssLayer: {
                name: 'primevue',
                order: 'tailwind-base, primevue, tailwind-utilities'
            }
        }
    },
    ripple: true,
});




// Verify Sentry initialization
setTimeout(() => {
    const client = Sentry.getClient();
    console.log('[SENTRY DEBUG] Client initialized:', !!client);
    console.log('[SENTRY DEBUG] Client DSN:', client?.getDsn?.()?.toString() || 'Not available');
    console.log('[SENTRY DEBUG] Feedback integration:', !!client?.getIntegrationByName?.('Feedback'));
    console.log('[SENTRY DEBUG] Options:', client?.getOptions?.() || 'Not available');
}, 100);

// Global error handlers for unhandled errors
window.addEventListener('error', (event) => {
    Sentry.captureException(event.error || new Error(event.message), {
        contexts: {
            error_event: {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                message: event.message
            }
        },
        tags: {
            error_type: 'javascript_error',
            environment: import.meta.env.MODE
        }
    });
});

window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason, {
        contexts: {
            promise_rejection: {
                reason: event.reason?.toString() || 'Unknown rejection',
                type: typeof event.reason
            }
        },
        tags: {
            error_type: 'unhandled_promise_rejection',
            environment: import.meta.env.MODE
        }
    });
});

app.use(router);
app.use(ToastService);
app.use(ConfirmationService);

app.directive('ripple', Ripple);
app.directive('styleclass', StyleClass);
app.directive('badge', BadgeDirective);
app.directive('tooltip', Tooltip);

const authStore = useAuthStore();
const automationStore = useAutomationStore();

// Bootstrap: authenticate, preload settings, then mount
void (async () => {
    try {
        await authStore.checkAuthStatus();
        if (authStore.isAuthenticated) {
            try {
                // Only fetch settings the user can read to avoid 403 redirects
                const canReadAutomation = authStore.hasPermission(PERMISSIONS.AUTOMATION_SETTINGS_READ);
                const canReadSettings = authStore.hasPermission(PERMISSIONS.SETTINGS_READ);
                if (canReadAutomation && canReadSettings) {
                    await automationStore.fetchAllSettings();
                    console.info('[BOOTSTRAP] Loaded automation + temperature settings.');
                } else {
                    if (canReadAutomation) {
                        await automationStore.fetchAutomationSettings();
                        console.info('[BOOTSTRAP] Loaded automation settings.');
                    }
                    if (canReadSettings) {
                        await automationStore.fetchTemperatureSettings();
                        console.info('[BOOTSTRAP] Loaded temperature settings.');
                    }
                }
            } catch (e) {
                console.warn('[BOOTSTRAP] Failed to load permitted settings, using defaults.', e);
            }
        }
    } finally {
        app.mount('#app');
        // If an old service-worker was registered from a previous PWA build, unregister it.
        if ('serviceWorker' in navigator) {
            void navigator.serviceWorker.getRegistrations().then(regs => {
                regs.forEach(r => {
                    if (r.active) {
                        console.info('[RMS] Unregistering stale service-worker:', r);
                    }
                    void r.unregister();
                });
            }).catch(err => console.warn('[RMS] SW unregister failed', err));
        }
    }
})();


// Mutex to prevent multiple concurrent logout attempts
let isLoggingOut = false;

// Network error toast debounce - prevents toast spam from repeated network failures
let lastNetworkErrorToast = 0;
const NETWORK_ERROR_TOAST_COOLDOWN = 30000; // 30 seconds between toasts

// Promise to track ongoing auth check - allows queuing requests during auth verification
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _pendingAuthCheck: Promise<void> | null = null;

// Auth debug logging utility - enable with: localStorage.setItem('rms:auth_debug', 'true')
const isAuthDebugEnabled = () => localStorage.getItem('rms:auth_debug') === 'true';
const authDebug = (message: string, data?: any) => {
    if (isAuthDebugEnabled()) {
        console.log(`[AUTH DEBUG] ${new Date().toISOString()} - ${message}`, data || '');
    }
};

// Debug helper for console - provides easy access to debug controls
// Usage: RMS_DEBUG.auth.enable() or RMS_DEBUG.list()
if (typeof window !== 'undefined') {
    (window as any).RMS_DEBUG = {
        auth: {
            enable: () => {
                localStorage.setItem('rms:auth_debug', 'true');
                console.log('[RMS_DEBUG] Auth debugging enabled. Refresh may be needed for full effect.');
            },
            disable: () => {
                localStorage.removeItem('rms:auth_debug');
                console.log('[RMS_DEBUG] Auth debugging disabled.');
            },
            status: () => localStorage.getItem('rms:auth_debug') === 'true'
        },
        api: {
            enable: () => {
                localStorage.setItem('rms:api_debug', 'true');
                console.log('[RMS_DEBUG] API debugging enabled.');
            },
            disable: () => {
                localStorage.removeItem('rms:api_debug');
                console.log('[RMS_DEBUG] API debugging disabled.');
            },
            status: () => localStorage.getItem('rms:api_debug') === 'true'
        },
        list: () => {
            console.log('[RMS_DEBUG] Debug flags:');
            console.log('  auth:', localStorage.getItem('rms:auth_debug') === 'true' ? 'ON' : 'off');
            console.log('  api:', localStorage.getItem('rms:api_debug') === 'true' ? 'ON' : 'off');
        },
        enableAll: () => {
            localStorage.setItem('rms:auth_debug', 'true');
            localStorage.setItem('rms:api_debug', 'true');
            console.log('[RMS_DEBUG] All debugging enabled.');
        },
        disableAll: () => {
            localStorage.removeItem('rms:auth_debug');
            localStorage.removeItem('rms:api_debug');
            console.log('[RMS_DEBUG] All debugging disabled.');
        },
        // Show last forbidden error details (useful for support)
        lastForbidden: () => {
            const data = sessionStorage.getItem('rms:last_forbidden');
            if (data) {
                console.log('[RMS_DEBUG] Last forbidden error:', JSON.parse(data));
            } else {
                console.log('[RMS_DEBUG] No forbidden errors recorded this session.');
            }
        },
        help: () => {
            console.log(`
[RMS_DEBUG] Available commands:
  RMS_DEBUG.auth.enable()    - Enable auth debugging
  RMS_DEBUG.auth.disable()   - Disable auth debugging
  RMS_DEBUG.api.enable()     - Enable API debugging
  RMS_DEBUG.api.disable()    - Disable API debugging
  RMS_DEBUG.list()           - Show all debug flag statuses
  RMS_DEBUG.enableAll()      - Enable all debugging
  RMS_DEBUG.disableAll()     - Disable all debugging
  RMS_DEBUG.lastForbidden()  - Show last 403 error details
  RMS_DEBUG.help()           - Show this help message
            `);
        }
    };
}

backendCall.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Get authStore at the top so it's available for all handlers
        const authStore = useAuthStore();

        // Check if this is a network error (offline)
        if (!error.response && error.message === 'Network Error') {
            console.error('Network error - device appears to be offline:', error);
            authDebug('Network error detected', { message: error.message });

            // Show toast notification for network errors (with debounce to prevent spam)
            const now = Date.now();
            if (now - lastNetworkErrorToast > NETWORK_ERROR_TOAST_COOLDOWN) {
                if (!window.location.href.includes('__playwright')) {
                    const toast = app.config.globalProperties.$toast;
                    if (toast) {
                        toast.add({
                            severity: 'warn',
                            summary: 'Connection Issue',
                            detail: 'Network error. Please check your connection and try again.',
                            life: 5000
                        });
                    }
                }
                lastNetworkErrorToast = now;
            }
            return Promise.reject(error);
        }

        // Only handle HTTP errors, not network errors
        if (error.response) {
            // Enhanced error logging
            const errorDetails = {
                url: error.config?.url,
                method: error.config?.method?.toUpperCase(),
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
                timestamp: new Date().toISOString()
            };

            console.error(`[API Error] ${errorDetails.method} ${errorDetails.url} - ${errorDetails.status} ${errorDetails.statusText}`, errorDetails);

            // Log to Sentry for all client and server errors (400+)
            if (error.response.status >= 400) {
                Sentry.captureException(error, {
                    contexts: {
                        api_error: errorDetails
                    },
                    tags: {
                        error_type: 'api_error',
                        status_code: error.response.status.toString(),
                        environment: import.meta.env.MODE
                    },
                    level: error.response.status >= 500 ? 'error' : 'warning'
                });
            }

            if (error.response.status === 401) {
                authDebug('401 received', { url: error.config?.url, lastAuthCheck: authStore.lastAuthCheck });

                // Prevent multiple concurrent logouts
                if (isLoggingOut) {
                    authDebug('Logout already in progress, skipping');
                    return Promise.reject(error);
                }

                const now = Date.now();
                // Handle edge case where lastAuthCheck is 0 (never checked or just logged in)
                const timeSinceLastAuth = authStore.lastAuthCheck > 0
                    ? now - authStore.lastAuthCheck
                    : Infinity; // Treat 0 as "never checked"

                authDebug('401 timing check', { timeSinceLastAuth, lastAuthCheck: authStore.lastAuthCheck });

                // If we checked auth very recently and still got 401, session is truly invalid
                if (timeSinceLastAuth < 5000) {
                    authDebug('Recent auth check failed, logging out');
                    isLoggingOut = true;
                    try {
                        await authStore.logout();
                    } finally {
                        isLoggingOut = false;
                    }
                } else {
                    // Haven't checked recently - try a fresh auth check first
                    authDebug('Forcing fresh auth check before logout');
                    await authStore.checkAuthStatus(true);
                    // If still not authenticated after fresh check, logout
                    if (!authStore.isAuthenticated) {
                        authDebug('Auth check confirmed not authenticated, logging out');
                        isLoggingOut = true;
                        try {
                            await authStore.logout();
                        } finally {
                            isLoggingOut = false;
                        }
                    } else {
                        authDebug('Auth check succeeded, not logging out');
                    }
                }
            } else if (error.response.status === 403) {
                authDebug('403 received', { url: error.config?.url });

                // Check if we've already tried refreshing for this request
                if (!error.config._retry) {
                    // Mark this request as having been retried
                    error.config._retry = true;

                    // Try refreshing the session to get latest permissions
                    const refreshResult = await authStore.refreshSession();
                    if (refreshResult.success) {
                        authDebug('403 refresh succeeded, retrying request');
                        // Retry the original request with updated permissions
                        return backendCall.request(error.config);
                    }
                    authDebug('403 refresh failed', { message: refreshResult.message });
                }

                // If we've already tried refreshing or refresh failed, redirect to forbidden
                // Try to extract required permission from backend error details, e.g. "(... (units:update))"
                let requiredPermission: string | undefined;
                try {
                    const details: string | undefined = error.response?.data?.error?.details || error.response?.data?.details;
                    if (typeof details === 'string') {
                        // Match the first resource:action token
                        const match = details.match(/[a-zA-Z_-]+:[a-zA-Z_*]+/);
                        if (match) {
                            requiredPermission = match[0];
                        }
                    }
                } catch { /* ignore parse errors */ }

                // Persist last forbidden details for support on devices without dev tools
                try {
                    const payload = {
                        url: errorDetails.url,
                        method: errorDetails.method,
                        status: errorDetails.status,
                        required: requiredPermission,
                        at: new Date().toISOString(),
                    };
                    sessionStorage.setItem('rms:last_forbidden', JSON.stringify(payload));
                } catch { /* ignore parse errors */ }

                if (requiredPermission) {
                    await router.push({ path: '/forbidden', query: { required: requiredPermission } });
                } else {
                    await router.push('/forbidden');
                }
            } else if (error.response.status >= 500) {
                // For 500 errors, show a user-friendly toast if not in test mode
                if (!window.location.href.includes('__playwright')) {
                    const toast = app.config.globalProperties.$toast;
                    if (toast) {
                        toast.add({
                            severity: 'error',
                            summary: 'Server Error',
                            detail: 'An unexpected error occurred. Please try again later.',
                            life: 5000
                        });
                    }
                }
            }
        } else {
            // Network errors - don't logout the user, just log the error
            console.error('Network error:', error);
        }

        return Promise.reject(error);
    }
);
